import { isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, map, merge } from 'rxjs';

/**
 * App-wide network connectivity status.
 *
 * Tracks the browser/webview online state via the standard `online`/`offline`
 * window events and exposes it as signals so any component can react to it.
 * SSR-safe: assumes online when there is no browser environment.
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkConnectivityService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private readonly online = signal(this.getInitialStatus());

  /** `true` while the device has a network connection. */
  readonly isOnline = this.online.asReadonly();

  /** `true` while the device has lost its network connection. */
  readonly isOffline = computed(() => !this.online());

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false)),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => this.online.set(status));
  }

  private getInitialStatus(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    return navigator.onLine;
  }
}
